/**
 * TaskDetailPane — F 86 Job A Stage 2 (A3)
 *
 * Right-side pane showing the full task: title (inline edit), notes (textarea),
 * due / scheduled date pickers, tags, status picker, timestamps, delete.
 *
 * Closes on Esc, click outside, or X. Saves on field blur.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import type { Task, TaskStatus } from '../../../types/models/Task';
import { TASK_VIEW_STATUSES, normalizeTaskStatus } from '../../../types/models/Task';
import type { TaskStore } from '../../../core/task/store';
// F 490 — the reminder clock for a date. See ReminderClock.tsx for why the
// wiring lives in a connector rather than in this pane.
import { ReminderClock } from '../common/ReminderClock';
// F 490 — the marker for a portal this pane opened. See reminderPortal.ts.
import { REMINDER_PORTAL_ATTR } from '../common/reminderPortal';
import './TaskDetailPane.css';

export interface TaskDetailPaneProps {
  /** The task to display. */
  task: Task;
  /** TaskStore instance to persist edits. */
  store: TaskStore;
  /** Close handler. */
  onClose: () => void;
}

const STATUS_LABEL: Record<TaskStatus, string> = {
  inbox: 'Inbox',
  today: 'Today',
  upcoming: 'Upcoming',
  anytime: 'Anytime',
  done: 'Done',
  open: 'Inbox',
  cancelled: 'Cancelled',
};

export const TaskDetailPane: React.FC<TaskDetailPaneProps> = ({ task, store, onClose }) => {
  const [title, setTitle] = useState(task.title);
  const [notes, setNotes] = useState(task.notes ?? '');
  const [due, setDue] = useState(task.due ?? '');
  const [scheduledDate, setScheduledDate] = useState(task.scheduledDate ?? '');
  const [tagsText, setTagsText] = useState((task.tags ?? []).join(', '));
  const [status, setStatus] = useState<TaskStatus>(task.status);
  const paneRef = useRef<HTMLDivElement | null>(null);

  // Save a single field. Skipped when the value hasn't changed.
  const saveField = useCallback(
    async (patch: Partial<Task>) => {
      const next: Task = {
        ...task,
        ...patch,
        updatedAt: new Date().toISOString(),
      };
      await store.save(next);
    },
    [task, store],
  );

  // Esc closes the pane.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  /**
   * Click outside the pane closes it.
   *
   * F 490 — AND A CLICK INSIDE A PORTAL THAT BELONGS TO THE PANE IS NOT
   * "OUTSIDE".
   *
   * MEASURED on production, and this bug was total: the reminder clock
   * opens a Mantine Popover, and Mantine renders a Popover's dropdown in a
   * PORTAL at the document body. So the popover's DOM is NOT a descendant
   * of the pane, and `paneRef.current.contains(target)` was FALSE for a
   * click on the popover's own Save button.
   *
   * The result: the mousedown closed the whole task pane, the popover
   * unmounted with it, and the `click` event had nothing left to land on.
   * The instrumented log was unambiguous — `pointerdown`, `mousedown`, and
   * NO `click` — and the popover closing LOOKED like the click working, so
   * a proof that asserted "the popover closed" passed while nothing was
   * saved. The whole pane was gone: `pane: false, clock: false`.
   *
   * THE FIX: the containment test walks the event's `composedPath()`, which
   * includes the portal's ancestors, and the portal is marked with a
   * data attribute so the pane can recognise it as its own.
   */
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (!paneRef.current) return;
      // A click inside the pane is inside.
      if (paneRef.current.contains(t)) return;
      // A click inside a portal that the pane OPENED is inside too. The
      // `composedPath` is used rather than `contains`, because the portal
      // is not a DOM descendant of the pane at all.
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      for (const node of path) {
        const el = node as HTMLElement;
        if (el?.getAttribute?.(REMINDER_PORTAL_ATTR) === 'true') return;
      }
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onClose]);

  return (
    <aside
      className="fc-task-detail-pane"
      data-testid="fc-task-detail-pane"
      ref={paneRef}
      role="complementary"
      aria-label="Task details"
    >
      <header className="fc-task-detail-pane__header">
        <button
          type="button"
          className="fc-task-detail-pane__close"
          aria-label="Close"
          data-testid="fc-task-detail-pane-close"
          onClick={onClose}
        >
          ×
        </button>
        <h3 className="fc-task-detail-pane__title-heading">Task</h3>
      </header>

      <div className="fc-task-detail-pane__body">
        {/* Title — inline editable */}
        <input
          type="text"
          className="fc-task-detail-pane__title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => title !== task.title && void saveField({ title })}
          data-testid="fc-task-detail-pane-title"
          aria-label="Title"
        />

        {/* Status picker */}
        <label className="fc-task-detail-pane__field">
          <span className="fc-task-detail-pane__label">Status</span>
          <select
            value={normalizeTaskStatus(status)}
            onChange={(e) => {
              const next = e.target.value as TaskStatus;
              setStatus(next);
              void saveField({
                status: next,
                completedAt: next === 'done' ? task.completedAt ?? new Date().toISOString() : null,
              });
            }}
            data-testid="fc-task-detail-pane-status"
          >
            {TASK_VIEW_STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </label>

        {/* Due date */}
        <label className="fc-task-detail-pane__field">
          <span className="fc-task-detail-pane__label">Due date</span>
          <input
            type="date"
            value={due}
            onChange={(e) => setDue(e.target.value)}
            onBlur={() => due !== (task.due ?? '') && void saveField({ due: due || null })}
            data-testid="fc-task-detail-pane-due"
          />
          {/* F 490 — a reminder can hang off a date. The clock sits next to
              the date input, so setting a time never disturbs the date the
              operator already chose. `due` is the date's identity within
              the task, which is what makes the reminder point at THIS date
              and not the task's other one. */}
          <ReminderClock
            parentNodeId={task.id}
            datePropertyId="due"
            dateValue={due}
            text={task.title}
            inline
          />
        </label>

        {/* Scheduled date */}
        <label className="fc-task-detail-pane__field">
          <span className="fc-task-detail-pane__label">Scheduled date</span>
          <input
            type="date"
            value={scheduledDate}
            onChange={(e) => setScheduledDate(e.target.value)}
            onBlur={() =>
              scheduledDate !== (task.scheduledDate ?? '') &&
              void saveField({ scheduledDate: scheduledDate || null })
            }
            data-testid="fc-task-detail-pane-scheduled"
          />
          {/* F 490 — the second date on the same task. The two reminders are
              SEPARATE nodes, each pointing at the same parent but at a
              different date. That is the whole reason the reminder is a
              node and not a field on the task. */}
          <ReminderClock
            parentNodeId={task.id}
            datePropertyId="scheduledDate"
            dateValue={scheduledDate}
            text={task.title}
            inline
          />
        </label>

        {/* Tags */}
        <label className="fc-task-detail-pane__field">
          <span className="fc-task-detail-pane__label">Tags (comma-separated)</span>
          <input
            type="text"
            value={tagsText}
            onChange={(e) => setTagsText(e.target.value)}
            onBlur={() => {
              const tags = tagsText
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean);
              const current = task.tags ?? [];
              if (
                tags.length !== current.length ||
                tags.some((t, i) => t !== current[i])
              ) {
                void saveField({ tags });
              }
            }}
            data-testid="fc-task-detail-pane-tags"
          />
        </label>

        {/* Notes */}
        <label className="fc-task-detail-pane__field fc-task-detail-pane__field--notes">
          <span className="fc-task-detail-pane__label">Notes</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => notes !== (task.notes ?? '') && void saveField({ notes })}
            rows={6}
            data-testid="fc-task-detail-pane-notes"
          />
        </label>

        {/* Timestamps */}
        <div className="fc-task-detail-pane__meta">
          <div>Created: {task.createdAt}</div>
          <div>Updated: {task.updatedAt}</div>
          {task.completedAt && <div>Completed: {task.completedAt}</div>}
          <div>fcid: {task.fcid}</div>
        </div>

        {/* Delete */}
        <button
          type="button"
          className="fc-task-detail-pane__delete"
          onClick={() => {
            void store.delete(task.id);
            onClose();
          }}
          data-testid="fc-task-detail-pane-delete"
        >
          Delete task
        </button>
      </div>
    </aside>
  );
};

export default TaskDetailPane;
